import React, { ComponentType, ReactNode, useMemo, useState } from 'react'
import { StyleProp, View, ViewStyle } from 'react-native'
import {
  Block,
  BlockComponentProps,
  BlockType,
  BlockPropsByType,
  DocletContent,
  DocletHostMedia
} from './types'
import { getBlockDefinition, isFullWidth } from './registry'
import { groupHighlightRuns } from './highlightRuns'
import { breakpoints, colors, layout } from './tokens'
import { DocletLayoutContext } from './DocletLayoutContext'
import { DocletAssetsContext } from './DocletAssetsContext'
import DocletFooter from './DocletFooter'

interface DocletPageProps {
  content: DocletContent
  editable?: boolean
  onChangeBlock?: <T extends BlockType>(
    id: string,
    props: BlockPropsByType[T]
  ) => void
  onFocusBlock?: (id: string) => void
  selectedBlockId?: string | null
  // Lets the builder wrap each block in its selection frame
  renderBlockFrame?: (
    block: Block,
    index: number,
    rendered: ReactNode
  ) => ReactNode
  pageStyle?: StyleProp<ViewStyle>
  /**
   * Editors for block types whose editing needs more than the renderer
   * provides (e.g. Text's rich text editor). Used only when `editable`.
   */
  editComponents?: {
    [T in BlockType]?: ComponentType<BlockComponentProps<T>>
  }
  // Shown inside the page when it has no blocks
  emptyPlaceholder?: ReactNode
  // Cloudinary cloud for uploaded images
  cloudinaryCloudName?: string | null
  // The host media records the Media blocks play (current links and status)
  hostMedia?: DocletHostMedia[]
}

/**
 * Renders a doclet: the page, its blocks in order, and the page fixtures
 * (currently only the copyright footer). Used read-only for previews and, with
 * `editable`, as the builder canvas.
 */
const DocletPage = ({
  content,
  editable = false,
  onChangeBlock,
  onFocusBlock,
  selectedBlockId = null,
  renderBlockFrame,
  pageStyle,
  editComponents,
  emptyPlaceholder = null,
  cloudinaryCloudName = null,
  hostMedia
}: DocletPageProps) => {
  const [isWide, setIsWide] = useState(true)
  const assets = useMemo(
    () => ({
      cloudinaryCloudName,
      hostMedia: Object.fromEntries(
        (hostMedia ?? []).map(record => [record.id, record])
      )
    }),
    [cloudinaryCloudName, hostMedia]
  )

  return (
    <DocletAssetsContext.Provider value={assets}>
      <DocletLayoutContext.Provider value={{ isWide }}>
        <View style={{ width: '100%', alignItems: 'center' }}>
          <View
            onLayout={({ nativeEvent }) =>
              setIsWide(nativeEvent.layout.width >= breakpoints.wide)
            }
            style={[
              {
                width: '100%',
                maxWidth: layout.pageMaxWidth,
                backgroundColor: colors.page,
                paddingHorizontal: layout.blockPadding,
                paddingVertical: layout.blockPadding * 2
              },
              pageStyle
            ]}
          >
            {!content.blocks.length && emptyPlaceholder}
            {groupHighlightRuns(content.blocks).map(run => {
              const blocks = run.entries.map(({ block, index }) => {
                const definition = getBlockDefinition(block.type)
                if (!definition) return null
                const Component = ((editable && editComponents?.[block.type]) ||
                  definition.Component) as ComponentType<BlockComponentProps>
                const rendered = (
                  <Component
                    block={block}
                    editable={editable}
                    onChange={props => onChangeBlock?.(block.id, props)}
                    onFocus={() => onFocusBlock?.(block.id)}
                    selected={block.id === selectedBlockId}
                  />
                )
                const framed = renderBlockFrame
                  ? renderBlockFrame(block, index, rendered)
                  : rendered
                return isFullWidth(block) ? (
                  <View key={block.id} style={fullWidth}>
                    {framed}
                  </View>
                ) : (
                  <React.Fragment key={block.id}>{framed}</React.Fragment>
                )
              })
              const key = run.entries[0].block.id
              return run.highlighted ? (
                <View key={key} style={highlightBand}>
                  {blocks}
                </View>
              ) : (
                <React.Fragment key={key}>{blocks}</React.Fragment>
              )
            })}
          </View>
          <DocletFooter />
        </View>
      </DocletLayoutContext.Provider>
    </DocletAssetsContext.Provider>
  )
}

// The highlighted band runs to the page's edges (cancelling the page's side
// padding) while its content keeps the same inset, so highlighted text lines
// up with the rest of the page instead of being indented by the band.
const highlightBand: ViewStyle = {
  backgroundColor: colors.band,
  marginHorizontal: -layout.blockPadding,
  paddingHorizontal: layout.blockPadding,
  paddingTop: layout.blockPadding,
  paddingBottom: layout.blockPadding / 2,
  marginBottom: layout.blockPadding
}

// A full-width block cancels the side padding of the page (or of the band it
// sits in, which has the same inset), running to the page's edges. Wraps the
// builder's frame too, so the selection outline matches the block.
const fullWidth: ViewStyle = {
  marginHorizontal: -layout.blockPadding
}

export default DocletPage
